import { Dispatch, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { isEmpty } from 'lodash-es';
import { Action } from 'redux';

import lang from '../../lib/language';
import { Week52HighLowStatusType } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { loadDiscoverChartData } from '../../lib/store/common-actions';
import { getWeek52HighLow as getWeek52HighLowCall } from '../../lib/store/fundamental/index';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import { isCallStatusReady } from '../../lib/util/error-handling/StatusByCallHelpers';

import { BaseTable, GoBackButton } from '../../components';
import NavButtons from '../../components/NavigationButtons/NavButtons';
import NoDataAvailable from '../../components/NoDataAvailable/NoDataAvailable';
import SkeletonLoaderRow from '../../components/WatchList/WatchListTable/SkeletonLoaderRow/SkeletonLoaderRow';
import { useIsMobile } from '../../hooks';
import PopularGroupTableItem from '../PopularGroups/PopularGroupTable/PopularGroupTableItem';
import { WEEK52_HIGH_LOW_HEADERS_MOBILE } from '../Week52GainersAndLosers/Week52GainersAndLosers.constants';

import styles from './FiftyTwoWeeksHighLow.module.scss';

const FiftyTwoWeeksHighLow = (props: ReduxProps) => {
  const { getWeek52HighLow, isWeek52HighLowReady, week52HighLow, getChartData } = props;
  const isReadyAndNoData = isWeek52HighLowReady && isEmpty(week52HighLow.content);

  const location = useLocation();
  const { state } = location;

  const [activeTab, setActiveTab] = useState<number>(
    state?.isTopGainersSelected ? 0 : 1,
  );

  const [changeStatus, setChangeStatus] = useState<Week52HighLowStatusType>(state?.isTopGainersSelected
    ? Week52HighLowStatusType.Gainers : Week52HighLowStatusType.Losers);
  const [page, setPage] = useState(0);

  const tabs = [
    { text: lang.commonEpicMoversGainers(), component: <></> },
    { text: lang.commonEpicMoversLosers(), component: <></> },
  ];

  const { isMobile } = useIsMobile();

  useEffect(() => {
    setPage(0);
    if (activeTab === 0) {
      setChangeStatus(Week52HighLowStatusType.Gainers);
    } else {
      setChangeStatus(Week52HighLowStatusType.Losers);
    }
  }, [activeTab]);

  useEffect(() => {
    getWeek52HighLow({ page, changeStatus });
  }, [getWeek52HighLow, page, changeStatus]);

  useEffect(() => {
    if (isWeek52HighLowReady) {
      const symbols = week52HighLow.content.map((item) => item.symbol).join(',');
      getChartData(symbols);
    }
  }, [getChartData, isWeek52HighLowReady, week52HighLow.content]);

  const week52HighLowHeaders = [
    lang.headerAccountBreakdownSymbolName(),
    lang.commonChart(),
    lang.commonLastPrice(),
    lang.commonDiscoverDailyPercentChange(),
    lang.commonDiscover52WeekPercentChange(),
  ];

  const tableHeaders = useMemo(
    () =>
      (isMobile ? WEEK52_HIGH_LOW_HEADERS_MOBILE : week52HighLowHeaders).map((item: string) => (
        // eslint-disable-next-line react-hooks/exhaustive-deps
        <th className={styles.header} key={item}>
          {item}
        </th>
      )),
    [], // eslint-disable-line react-hooks/exhaustive-deps

  );

  const onViewMoreClick = () => {
    setPage(page + 1);
  };

  return (
    <div className={styles.wrapper}>
      {!isMobile && (
        <div className={styles.goBackButton}>
          <GoBackButton />
        </div>
      )}
      <div className={classNames(styles.titleSection, { [styles.noMargin]: isMobile })}>
        <div className={styles.title}>{lang.commonWeek52GainersAndLosers()}</div>
        <div className={styles.subtitle}>{lang.commonWeek52GainersAndLosersStockMakingHighLow()}</div>
      </div>
      <div className={styles.tableWrapper}>
        <NavButtons tabs={tabs} sendCurrentTabIndex={setActiveTab} initialActiveTab={activeTab} />
        <div className={styles.table}>
          <BaseTable isDiscoverTable>
            {isReadyAndNoData ? (
              <NoDataAvailable
                className={styles.noDataToDisplay}
                isNoDataIcon
                title={lang.commonDiscover52WeekNoData()}
              />
            ) : (
              <>
                <thead>
                  <tr>{tableHeaders}</tr>
                </thead>
                <tbody>
                  {week52HighLow.content.length === 0 ? (
                    <>
                      <SkeletonLoaderRow />
                      <SkeletonLoaderRow />
                      <SkeletonLoaderRow />
                      <SkeletonLoaderRow />
                    </>
                  ) : (
                    <>
                      {week52HighLow.content.map((item, index) => (
                        <PopularGroupTableItem isWeek52Item key={index as number} item={item} />
                      ))}
                    </>
                  )}
                </tbody>
              </>
            )}
          </BaseTable>
          {page + 1 < week52HighLow.totalPages && (
            <div className={styles.viewMore}>
              <button type="button" onClick={onViewMoreClick} className={styles.viewMoreButton} disabled={!isWeek52HighLowReady}>
                {lang.commonLoadMore()}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isWeek52HighLowReady: isCallStatusReady(
    FundamentalCall.getWeek52HighLow,
    state.fundamental.statusByCall,
  ),
  week52HighLow: state.fundamental.week52HighLow,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getWeek52HighLow: (params) => dispatch(getWeek52HighLowCall(params)),
  getChartData: (symbols: string) => dispatch(loadDiscoverChartData(symbols)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(FiftyTwoWeeksHighLow);
